import React, { useRef, useEffect } from 'react';

class CanvasImageGenerator {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.loadedFonts = new Set(); // Cache des polices déjà chargées
  }

  // Charger une police Google Font avant de l'utiliser
  async loadFont(fontFamily) {
    // Vérifier que fontFamily est une chaîne valide
    if (!fontFamily || typeof fontFamily !== 'string') {
      // Utiliser une police par défaut si fontFamily n'est pas valide
      fontFamily = 'Arial, sans-serif';
    }

    // Si la police est déjà chargée, ne rien faire
    if (this.loadedFonts.has(fontFamily)) {
      return;
    }

    // Extraire le nom de la police (sans les guillemets et fallbacks)
    const fontName = fontFamily.replace(/['"]/g, '').split(',')[0].trim();
    
    // Attendre que les polices soient chargées
    await document.fonts.ready;
    
    // Vérifier si la police est disponible
    // Pour les polices Google Fonts, on vérifie si elles sont chargées
    const isLoaded = document.fonts.check(`12px ${fontFamily}`);
    
    // Liste des polices Google Fonts supportées
    const googleFonts = [
      'Roboto', 
      'Roboto Condensed',
      'Open Sans',
      'Montserrat',
      'Lato',
      'Poppins',
      'Raleway',
      'Oswald',
      'Playfair Display',
      'Merriweather'
    ];
    
    if (!isLoaded && googleFonts.includes(fontName)) {
      // Les polices Google Fonts sont chargées via CSS, mais le Canvas peut avoir besoin
      // d'un délai supplémentaire. On attend un peu plus.
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Marquer la police comme chargée (même si elle n'est pas disponible, on utilisera le fallback)
    this.loadedFonts.add(fontFamily);
  }

  // Initialiser le canvas
  initCanvas(width = 1080, height = 1080) {
    // Créer un nouveau canvas ou réutiliser l'existant
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
    }
    
    // Mettre à jour les dimensions si nécessaire
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    
    // Obtenir le contexte
    if (!this.ctx) {
      this.ctx = this.canvas.getContext('2d');
    }
    
    // IMPORTANT: Nettoyer le canvas avant de dessiner
    this.ctx.clearRect(0, 0, width, height);
    
    // Configuration de base pour la qualité
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
    
    return this.canvas;
  }

  // Calculer les dimensions selon le format
  getDimensionsForFormat(format, customWidth = null, customHeight = null) {
    if (customWidth && customHeight) {
      return { width: customWidth, height: customHeight };
    }

    const baseWidth = 1080;
    
    switch (format) {
      case 'square':
        return { width: baseWidth, height: baseWidth }; // 1:1 = 1080x1080
      case 'story':
        return { width: baseWidth, height: 1920 }; // 9:16 = 1080x1920
      case 'landscape':
        return { width: baseWidth, height: 566 }; // 1.91:1 = 1080x566
      case 'portrait':
        return { width: baseWidth, height: 1350 }; // 4:5 = 1080x1350
      case 'carousel':
        // Les carrousels utilisent le format square par défaut
        return { width: baseWidth, height: baseWidth };
      default:
        return { width: baseWidth, height: baseWidth };
    }
  }

  // Générer une image à partir d'un template
  async generateImage(template, data, options = {}) {
    const { quality = 0.95 } = options;
    
    // Calculer les dimensions selon le format du template
    const dimensions = this.getDimensionsForFormat(
      template.format,
      options.width,
      options.height
    );

    // Initialiser le canvas avec les bonnes dimensions
    // Cela nettoie automatiquement le canvas
    this.initCanvas(dimensions.width, dimensions.height);

    try {
      const { width, height } = dimensions;
      
      // 1. Dessiner le background (sauf si une image de fond est fournie)
      if (!data.backgroundImage) {
        await this.drawBackground(template, width, height);
      }

      // 2. Dessiner l'image de fond si présente (remplace le fond du template)
      if (data.backgroundImage) {
        // Permettre de personnaliser l'opacité de l'overlay (par défaut 0.15 = 15%)
        const overlayOpacity = data.overlayOpacity !== undefined ? data.overlayOpacity : 0.15;
        await this.drawBackgroundImage(data.backgroundImage, width, height, overlayOpacity);
        // L'overlay est déjà ajouté dans drawBackgroundImage
      }

      // 3. Dessiner les textes selon le layout du template
      // IMPORTANT: S'assurer que le texte est dessiné APRÈS l'overlay
      await this.drawTexts(template, data, width, height);

      // 4. Ajouter le watermark/signature
      this.drawWatermark(template, width, height);

      // 5. Convertir en blob pour upload
      return new Promise((resolve) => {
        this.canvas.toBlob(resolve, 'image/jpeg', quality);
      });

    } catch (error) {
      console.error('Erreur génération Canvas:', error);
      throw error;
    }
  }

  // Dessiner le background (couleur ou dégradé)
  drawBackground(template, width, height) {
    const { background } = template.style;
    
    if (background.startsWith('linear-gradient')) {
      // Parser le gradient CSS et le convertir en gradient Canvas
      this.drawGradientBackground(background, width, height);
    } else {
      // Couleur unie
      this.ctx.fillStyle = background;
      this.ctx.fillRect(0, 0, width, height);
    }
  }

  // Dessiner un gradient background
  drawGradientBackground(cssGradient, width, height) {
    // Parser "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    const gradientMatch = cssGradient.match(/linear-gradient\(([^)]+)\)/);
    if (!gradientMatch) return;

    const params = gradientMatch[1].split(',').map(p => p.trim());
    const angle = parseInt(params[0]) || 0;
    
    // Convertir l'angle CSS en coordonnées Canvas
    const angleRad = (angle - 90) * Math.PI / 180;
    const x1 = width / 2 - Math.cos(angleRad) * width / 2;
    const y1 = height / 2 - Math.sin(angleRad) * height / 2;
    const x2 = width / 2 + Math.cos(angleRad) * width / 2;
    const y2 = height / 2 + Math.sin(angleRad) * height / 2;

    const gradient = this.ctx.createLinearGradient(x1, y1, x2, y2);
    
    // Ajouter les couleurs
    for (let i = 1; i < params.length; i++) {
      const colorMatch = params[i].match(/(#[a-fA-F0-9]{6}|#[a-fA-F0-9]{3}|\w+)\s*(\d+%)?/);
      if (colorMatch) {
        const color = colorMatch[1];
        const position = colorMatch[2] ? parseInt(colorMatch[2]) / 100 : (i - 1) / (params.length - 2);
        gradient.addColorStop(position, color);
      }
    }

    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);
  }

  // Dessiner une image de fond
  async drawBackgroundImage(imageUrl, width, height, overlayOpacity = 0.15) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Dessiner l'image à pleine opacité
        this.ctx.drawImage(img, 0, 0, width, height);

        // Ajouter un overlay noir très léger pour améliorer la lisibilité du texte
        // Opacité réduite pour ne pas masquer l'image (par défaut 15%)
        if (overlayOpacity > 0) {
          this.ctx.fillStyle = `rgba(0, 0, 0, ${overlayOpacity})`;
          this.ctx.fillRect(0, 0, width, height);
        }
        
        resolve();
      };
      
      img.onerror = reject;
      img.src = imageUrl;
    });
  }

  // Dessiner tous les textes selon le template
  async drawTexts(template, data, width, height) {
    // Vérifier que template.fields existe et n'est pas vide
    if (!template.fields || template.fields.length === 0) {
      return;
    }
    
    // Charger toutes les polices nécessaires en premier
    const fontsToLoad = new Set();
    template.fields.forEach(field => {
      const layout = template.layout[field];
      if (!layout) {
        return;
      }
      const customFontFamily = data.customFontFamily?.[field] || data.customFontFamily || null;
      // S'assurer que fontFamily est toujours une chaîne valide
      let fontFamily = customFontFamily || template.style?.fontFamily || 'Arial, sans-serif';
      // Vérifier que fontFamily est bien une chaîne
      if (typeof fontFamily !== 'string') {
        fontFamily = 'Arial, sans-serif';
      }
      fontsToLoad.add(fontFamily);
    });
    
    // Charger toutes les polices en parallèle
    await Promise.all(Array.from(fontsToLoad).map(font => this.loadFont(font)));
    
    // Dessiner les textes
    for (const field of template.fields) {
      const layout = template.layout[field];
      if (!layout) {
        continue;
      }
      
      const text = data[field] || `[${field.toUpperCase()}]`;
      
      // Récupérer les paramètres personnalisés pour ce champ s'ils existent
      const customFontFamily = data.customFontFamily?.[field] || data.customFontFamily || null;
      const customFontSize = data.customFontSize?.[field] || data.customFontSize || null;
      
      await this.drawText(text, layout, template.style, width, height, {
        customTextColor: data.customTextColor,
        customFontFamily,
        customFontSize
      });
    }
  }

  // Dessiner un texte avec positioning
  async drawText(text, layout, style, width, height, customOptions = {}) {
    const { customTextColor = null, customFontFamily = null, customFontSize = null } = customOptions;
    
    // Vérifier que le texte n'est pas vide
    if (!text || text.trim() === '') {
      return;
    }
    
    // Configuration du texte
    // Utiliser la taille personnalisée si fournie, sinon calculer depuis le layout
    let fontSize;
    if (customFontSize !== null && customFontSize !== undefined && customFontSize !== 0) {
      // Taille personnalisée fournie : utiliser directement (déjà en pixels pour 1080px)
      // La valeur est en pixels pour une image de 1080px de largeur
      fontSize = this.scaleFontSize(customFontSize, width);
    } else {
      // Utiliser la taille du template (avec fallback si layout.size n'existe pas)
      const layoutSize = layout?.size || 'medium';
      fontSize = this.calculateFontSize(layoutSize, width);
    }
    
    if (fontSize <= 0) {
      return;
    }
    
    const fontWeight = layout.weight || 'normal';
    
    // Utiliser la police personnalisée si fournie, sinon celle du template, sinon Arial
    let fontFamily = customFontFamily || style?.fontFamily || 'Arial, sans-serif';
    // S'assurer que fontFamily est toujours une chaîne valide
    if (typeof fontFamily !== 'string') {
      fontFamily = 'Arial, sans-serif';
    }
    
    // Charger la police si nécessaire (pour les polices Google Fonts)
    await this.loadFont(fontFamily);
    
    // S'assurer que le contexte est bien configuré avant de dessiner
    this.ctx.save();
    
    this.ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    // Utiliser la couleur personnalisée si fournie, sinon la couleur du template
    this.ctx.fillStyle = customTextColor || style.textColor || '#000000';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    // Position basée sur les pourcentages du layout
    const x = (layout.x / 100) * width;
    const y = (layout.y / 100) * height;

    // Gérer le texte multi-ligne si nécessaire
    this.drawMultilineText(text, x, y, fontSize, width * 0.8);
    
    // Restaurer le contexte
    this.ctx.restore();
  }

  // Calculer la taille de police basée sur la largeur du canvas
  // Utilise un ratio constant basé sur 1080px (taille finale) pour garantir la cohérence
  calculateFontSize(cssSize, canvasWidth) {
    // Vérifier que cssSize est valide
    if (!cssSize) {
      return Math.round(54 * (canvasWidth / 1080)); // Défaut : medium
    }

    // Convertir en chaîne si ce n'est pas déjà le cas
    const sizeStr = String(cssSize);
    
    // Ratio de référence : 1080px est la taille finale standard
    const referenceWidth = 1080;
    const scaleRatio = canvasWidth / referenceWidth;
    
    // Convertir les tailles CSS en pixels pour le canvas
    // Les valeurs sont basées sur 1080px et sont ensuite scalées
    const sizeMap = {
      'small': 32 * scaleRatio,      // ~32px à 1080px
      'medium': 54 * scaleRatio,     // ~54px à 1080px
      'large': 86 * scaleRatio,      // ~86px à 1080px
      'x-large': 130 * scaleRatio    // ~130px à 1080px
    };

    if (sizeMap[sizeStr]) {
      return Math.round(sizeMap[sizeStr]);
    }

    // Si c'est un nombre, le traiter directement
    if (typeof cssSize === 'number' || !isNaN(parseFloat(sizeStr))) {
      const numSize = typeof cssSize === 'number' ? cssSize : parseFloat(sizeStr);
      return Math.round(numSize * scaleRatio);
    }

    // Si c'est déjà en pixels (e.g., "24px"), convertir en taille à 1080px puis scaler
    const pxMatch = sizeStr.match(/(\d+)px/);
    if (pxMatch) {
      const baseSize = parseInt(pxMatch[1]);
      // Convertir la taille en équivalent 1080px puis scaler
      return Math.round(baseSize * scaleRatio);
    }

    return Math.round(54 * scaleRatio); // Défaut : medium
  }

  // Scaler une taille de police personnalisée selon la largeur du canvas
  // La taille personnalisée est en pixels pour une image de 1080px de largeur
  scaleFontSize(fontSize, canvasWidth) {
    // Vérifier que fontSize est valide
    if (fontSize === null || fontSize === undefined) {
      return this.calculateFontSize('medium', canvasWidth);
    }

    const referenceWidth = 1080;
    const scaleRatio = canvasWidth / referenceWidth;
    
    // Si c'est un nombre, le traiter comme une taille en pixels à 1080px
    if (typeof fontSize === 'number') {
      const scaledSize = Math.round(fontSize * scaleRatio);
      // S'assurer que la taille minimale est respectée
      return Math.max(12, scaledSize);
    }
    
    // Si c'est une string avec "px", extraire le nombre
    const fontSizeStr = String(fontSize);
    const pxMatch = fontSizeStr.match(/(\d+)px/);
    if (pxMatch) {
      const baseSize = parseInt(pxMatch[1]);
      const scaledSize = Math.round(baseSize * scaleRatio);
      return Math.max(12, scaledSize);
    }
    
    // Sinon, utiliser calculateFontSize
    return this.calculateFontSize(fontSize, canvasWidth);
  }

  // Dessiner du texte multi-ligne
  drawMultilineText(text, x, y, fontSize, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    // Découper en lignes
    words.forEach(word => {
      const testLine = currentLine + word + ' ';
      const metrics = this.ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine !== '') {
        lines.push(currentLine.trim());
        currentLine = word + ' ';
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine.trim() !== '') {
      lines.push(currentLine.trim());
    }

    // Dessiner chaque ligne avec un contour pour améliorer la lisibilité
    const lineHeight = fontSize * 1.2;
    const startY = y - ((lines.length - 1) * lineHeight) / 2;

    lines.forEach((line, index) => {
      const lineY = startY + (index * lineHeight);
      
      // Dessiner le contour du texte (stroke) pour améliorer la lisibilité
      // Utiliser un contour plus épais et plus visible
      this.ctx.save();
      this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
      this.ctx.lineWidth = Math.max(3, fontSize * 0.08);
      this.ctx.lineJoin = 'round';
      this.ctx.miterLimit = 2;
      this.ctx.strokeText(line, x, lineY);
      this.ctx.restore();
      
      // Dessiner le texte principal par-dessus avec la couleur définie
      this.ctx.fillText(line, x, lineY);
    });
  }

  // Ajouter le watermark
  drawWatermark(template, width, height) {
    const watermark = '@causecommune93.1';
    // Utiliser le même ratio de référence pour le watermark
    const referenceWidth = 1080;
    const scaleRatio = width / referenceWidth;
    const fontSize = Math.round(27 * scaleRatio); // ~27px à 1080px
    
    this.ctx.font = `600 ${fontSize}px Arial, sans-serif`;
    this.ctx.fillStyle = template.style.accentColor || '#ffffff';
    this.ctx.textAlign = 'right';
    this.ctx.textBaseline = 'bottom';
    
    const margin = width * 0.05;
    this.ctx.fillText(watermark, width - margin, height - margin);
  }

  // Méthode utilitaire pour prévisualiser
  getPreviewDataURL() {
    return this.canvas.toDataURL('image/jpeg', 0.8);
  }
}

// Hook React pour utiliser le générateur
export const useCanvasGenerator = () => {
  const generatorRef = useRef(new CanvasImageGenerator());
  
  const generateImage = async (template, data, options) => {
    return await generatorRef.current.generateImage(template, data, options);
  };

  const getPreviewURL = (template, data, options) => {
    // Générer pour prévisualisation (plus petit, plus rapide)
    // Utiliser les mêmes dimensions que la prévisualisation HTML pour cohérence
    const previewOptions = { ...options, width: 400, height: 400, quality: 0.7 };
    return generatorRef.current.generateImage(template, data, previewOptions)
      .then(() => generatorRef.current.getPreviewDataURL());
  };

  return { generateImage, getPreviewURL };
};

// Composant React pour prévisualisation en temps réel
export const CanvasPreview = ({ template, data, className }) => {
  const canvasRef = useRef(null);
  const { getPreviewURL } = useCanvasGenerator();

  useEffect(() => {
    if (template && canvasRef.current) {
      getPreviewURL(template, data).then(dataURL => {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.src = dataURL;
      }).catch(console.error);
    }
  }, [template, data, getPreviewURL]);

  // Calculer l'aspect ratio selon le format
  const getAspectRatio = (format) => {
    switch (format) {
      case 'square':
        return 1; // 1:1
      case 'story':
        return 9/16; // 9:16
      case 'landscape':
        return 1080/566; // 1.91:1
      case 'portrait':
        return 1080/1350; // 4:5
      case 'carousel':
        return 1; // 1:1 par défaut
      default:
        return 1;
    }
  };

  const aspectRatio = template?.format ? getAspectRatio(template.format) : 1;

  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={400 / aspectRatio}
      className={className}
      style={{ 
        maxWidth: '100%', 
        height: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}
    />
  );
};

export default CanvasImageGenerator; 